// src/stores/hotel.js
import { defineStore } from 'pinia'

export const useHotelStore = defineStore('hotel', {
  state: () => ({
    rooms: {},                    // { "1.1": { "1": { FIO: "", PASS: "", PHONE: "", BIRTHDATE: "" } }, ... }
    loading: false,
    error: null,
    lastLoadedAt: null,

    // Поля для руководителя и дат
    leaderFio: '',
    leaderPassport: '',
    groupName: '',
    arrivalDate: '',     // YYYY-MM-DD
    arrivalTime: '',     // HH:mm
    arrivalPlace: '',
    departureDate: '',
    departureTime: '',
    departurePlace: '',

    // Состояние модального окна
    modalOpen: false,
    selectedRoomKey: null,
    localRoomData: {},            // временная копия данных выбранной комнаты
    updateLoading: false,
    updateSuccess: false,
    updateError: null
  }),

  getters: {
    hasRooms: state => Object.keys(state.rooms).length > 0,

    sortedRoomKeys: state => {
      return Object.keys(state.rooms).sort((a, b) => {
        const [typeA, numA] = a.split('.').map(Number)
        const [typeB, numB] = b.split('.').map(Number)
        if (typeA !== typeB) return typeA - typeB
        return numA - numB
      })
    },

    totalRooms: state => Object.keys(state.rooms).length,

    totalBeds: state => {
      let count = 0
      Object.values(state.rooms).forEach(beds => {
        count += Object.keys(beds).length
      })
      return count
    },

    occupiedBeds: state => {
      let count = 0
      Object.values(state.rooms).forEach(beds => {
        Object.values(beds).forEach(person => {
          if (person?.FIO?.trim()) count++
        })
      })
      return count
    },

    isLeaderFormValid: state => {
      return (
        state.leaderFio.trim() !== '' &&
        state.leaderPassport.trim() !== '' &&
        state.groupName.trim() !== '' &&
        state.arrivalDate !== '' &&
        state.arrivalTime !== '' &&
        state.arrivalPlace.trim() !== '' &&
        state.departureDate !== '' &&
        state.departureTime !== '' &&
        state.departurePlace.trim() !== ''
      )
    }
  },

  actions: {
    setRooms(data) {
      this.rooms = { ...data }
      this.lastLoadedAt = new Date()
      this.error = null
    },

    startLoading() {
      this.loading = true
      this.error = null
    },

    finishLoading() {
      this.loading = false
    },

    setError(message) {
      this.error = message
      this.loading = false
    },

    clear() {
      this.rooms = {}
      this.error = null
      this.lastLoadedAt = null
      this.loading = false
      this.closeModal()

      this.leaderFio = ''
      this.leaderPassport = ''
      this.groupName = ''
      this.arrivalDate = ''
      this.arrivalTime = ''
      this.arrivalPlace = ''
      this.departureDate = ''
      this.departureTime = ''
      this.departurePlace = ''
    },

    updatePerson(roomKey, bedKey, updates) {
      if (this.rooms[roomKey]?.[bedKey]) {
        this.rooms[roomKey][bedKey] = {
          ...this.rooms[roomKey][bedKey],
          ...updates
        }
      }
    },

    openModal(roomKey) {
      this.selectedRoomKey = roomKey

      const original = this.rooms[roomKey] || {}

      this.localRoomData = {}
      Object.keys(original).forEach(bedKey => {
        const person = original[bedKey] || {}
        this.localRoomData[bedKey] = {
          FIO:       person.FIO       || '',
          PASS:      person.PASS      || '',
          PHONE:     person.PHONE     || '',
          BIRTHDATE: person.BIRTHDATE || ''
        }
      })

      this.modalOpen = true
      this.updateError = null
      this.updateSuccess = false
      this.updateLoading = false
    },

    closeModal() {
      this.modalOpen = false
      this.selectedRoomKey = null
      this.localRoomData = {}
      this.updateError = null
      this.updateSuccess = false
      this.updateLoading = false
    },

    // ──────────────────────────────────────────────────────────────
    async loadHotelRooms(token, roomCounts, recaptcha = null) {
      if (this.loading) return

      this.startLoading()

      try {
        let recaptchaToken = ''

        if (recaptcha && recaptcha.executeRecaptcha) {
          await recaptcha.recaptchaLoaded()
          recaptchaToken = await recaptcha.executeRecaptcha('load_hotel_rooms')
        } else {
          console.warn('reCAPTCHA helpers not provided — skipping verification for loadHotelRooms')
          // В production можно: throw new Error('reCAPTCHA required')
        }

        const payload = {
          token,
          recaptcha_token: recaptchaToken,
          ...roomCounts
        }

        const response = await fetch("https://dancerus.ru/api/get_rooms.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })

        if (!response.ok) {
          throw new Error(`Сервер ответил ${response.status}`)
        }

        const data = await response.json()

        const normalized = {}
        Object.entries(data || {}).forEach(([roomKey, beds]) => {
          normalized[roomKey] = {}
          Object.entries(beds || {}).forEach(([bedKey, person]) => {
            normalized[roomKey][bedKey] = {
              FIO:       person?.FIO       || '',
              PASS:      person?.PASS      || '',
              PHONE:     person?.PHONE     || '',
              BIRTHDATE: person?.BIRTHDATE || ''
            }
          })
        })

        this.setRooms(normalized)
      } catch (err) {
        this.setError(`Не удалось загрузить номера: ${err.message}`)
        console.error('Ошибка загрузки номеров:', err)
      } finally {
        this.finishLoading()
      }
    },

    // ──────────────────────────────────────────────────────────────
    async saveRoom(token, recaptcha = null) {
      if (this.updateLoading) return

      this.updateLoading = true
      this.updateError = null
      this.updateSuccess = false

      try {
        let recaptchaToken = ''

        if (recaptcha && recaptcha.executeRecaptcha) {
          await recaptcha.recaptchaLoaded()
          recaptchaToken = await recaptcha.executeRecaptcha('update_room')
        } else {
          console.warn('reCAPTCHA helpers not provided — skipping verification for saveRoom')
        }

        // Оптимистическое обновление
        Object.entries(this.localRoomData).forEach(([bedKey, person]) => {
          this.updatePerson(this.selectedRoomKey, bedKey, person)
        })

        const payload = {
          token,
          recaptcha_token: recaptchaToken,
          roomKey: this.selectedRoomKey,
          beds: this.localRoomData
        }

        const response = await fetch("https://dancerus.ru/api/update_room.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })

        if (!response.ok) {
          throw new Error(`Сервер ответил ${response.status}`)
        }

        const data = await response.json()

        if (!data?.success) {
          throw new Error(data?.error || 'Ошибка обновления комнаты')
        }

        this.updateSuccess = true
        setTimeout(() => this.closeModal(), 1500)
      } catch (err) {
        this.updateError = `Не удалось сохранить комнату: ${err.message}`
        console.error('Ошибка сохранения комнаты:', err)
      } finally {
        this.updateLoading = false
      }
    },

    // ──────────────────────────────────────────────────────────────
    async sendHotelRooms(token, recaptcha = null) {
      if (this.loading) return

      this.startLoading()
      this.error = null

      try {
        let recaptchaToken = ''

        if (recaptcha && recaptcha.executeRecaptcha) {
          await recaptcha.recaptchaLoaded()
          recaptchaToken = await recaptcha.executeRecaptcha('send_hotel_data')
        } else {
          console.warn('reCAPTCHA helpers not provided — skipping verification for sendHotelRooms')
        }

        const payload = {
          token,
          recaptcha_token: recaptchaToken,
          leader_fio: this.leaderFio.trim(),
          leader_passport: this.leaderPassport.trim(),
          group_name: this.groupName.trim(),
          arrival_date: this.arrivalDate,
          arrival_time: this.arrivalTime,
          arrival_place: this.arrivalPlace.trim(),
          departure_date: this.departureDate,
          departure_time: this.departureTime,
          departure_place: this.departurePlace.trim()
        }

        const response = await fetch("https://dancerus.ru/api/send_rooms.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })

        if (!response.ok) {
          throw new Error(`Сервер ответил ${response.status}`)
        }

        const data = await response.json()

        if (!data?.success) {
          throw new Error(data?.error || 'Ошибка отправки данных')
        }

        this.clear()
      } catch (err) {
        this.setError(`Не удалось отправить данные в отель: ${err.message}`)
        console.error('Ошибка отправки:', err)
      } finally {
        this.finishLoading()
      }
    }
  }
})